import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-location';
import { notification } from 'antd';
import { useMutation } from '@apollo/client';
import _ from 'lodash';
import ScreenWrapper from '../../../components/common/ScreenWrapper';
import DukePaymentButton, {
  DukePaymentButtonRef,
} from 'components/duke-payment/duke-payment';
import { Modal } from 'components';
import { usePaymentChannels } from '../hooks/usePayChannels';
import {
  CountryCode,
  PaystackGhanaBankType,
  PaystackMobileMoneyProvider,
} from '_graphql/__generated__/globalTypes';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { ICart, ICustomer } from '../../../utils/types';
import { LocationGenerics } from 'router/location';
import { useCurrentUser } from '_graphql/cache/auth';
import CustomerCard from './customer-card';
import DeliveryDetailsCard from './delivery-details-card';
import SummaryCard from './summary-card';
import { calculateCheckoutTotals } from '../utils/summary-card-helper';
import {
  calculateAppliedDiscount,
  calculateFinalAmount,
} from '../utils/checkout-calculations';
import { setPaymentMethod, clearCart } from '../../../redux/slice/cart-slice';
import { createOrder as createOrderMutation } from '_graphql/mutation/order';
import {
  CreateOrder,
  CreateOrderVariables,
} from '_graphql/mutation/__generated__/CreateOrder';
import { Button, Input } from '@mantine/core';

enum OrderPaymentMethod {
  Cash = 'CASH',
  PaymentOnline = 'PAYMENT_ONLINE',
  PaymentOnDelivery = 'PAYMENT_ON_DELIVERY',
}

enum OrderPaymentStatus {
  Paid = 'PAID',
  PartiallyPaid = 'PARTIALLY_PAID',
  Pending = 'PENDING',
  Refunded = 'REFUNDED',
  SplitPayment = 'SPLIT_PAYMENT',
}

enum OrderDeliveryType {
  Delivery = 'DELIVERY',
  Pickup = 'PICKUP',
}

const Checkout = () => {
  const dispatch = useAppDispatch();
  const { cart } = useAppSelector((state) => state.cart);
  const user = useCurrentUser();
  const navigate = useNavigate<LocationGenerics>();

  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [provider, setProvider] = useState('');
  const [accountName, setAccountName] = useState('');
  const [isInitializingPayment, setIsInitializingPayment] = useState(false);
  const country = user?.country ?? 'GH';

  const { verifyAccount, verifying, getChannels, data, loading } =
    usePaymentChannels();

  useEffect(() => {
    getChannels({
      variables: {
        input: {
          country: country as CountryCode,
          filter: {
            type: country === 'GH' ? PaystackGhanaBankType.mobile_money : null,
          },
        },
      },
    });
  }, [country]);

  useEffect(() => {
    if (phoneNumber.length >= 10 && provider) {
      verifyAccount({
        variables: {
          input: {
            account_number: phoneNumber,
            bank_code: provider,
          },
        },
      }).then(({ data }) => {
        if (data?.verifyAccountDetails) {
          setAccountName(data?.verifyAccountDetails?.account_name);
        } else {
          setAccountName('');
        }
      });
    }
  }, [provider, phoneNumber]);

  // set payment method to pay_online if cart has payment method pay_online
  useEffect(() => {
    if (cart?.paymentMethod === 'pay_online') {
      dispatch(setPaymentMethod('pay_online'));
    }
  }, [cart?.paymentMethod]);

  const paymentRef = useRef<DukePaymentButtonRef>(null);

  const isFreeDelivery = false;

  const _isPickup = useMemo(() => {
    return cart?.pickup != null;
  }, [cart?.pickup]);

  const { totalToCollect } = useMemo(
    () => calculateCheckoutTotals(cart as ICart, isFreeDelivery),
    [cart, isFreeDelivery]
  );

  const finalAmount = useMemo(
    () => calculateFinalAmount(cart as ICart, isFreeDelivery),
    [cart, isFreeDelivery]
  );

  const appliedDiscount = useMemo(
    () => calculateAppliedDiscount(cart as ICart, isFreeDelivery),
    [cart, isFreeDelivery]
  );

  const firstProductCurrency = cart?.products?.[0]?.currency ?? 'GHS';

  const isPayOnDelivery =
    cart?.paymentMethod === 'pay_on_delivery' ||
    cart?.paymentMethod === 'pay_at_pickup';

  const paymentMethod = cart?.paymentMethod ?? 'pay_on_delivery';

  const [createOrder, { loading: ordering }] = useMutation<
    CreateOrder,
    CreateOrderVariables
  >(createOrderMutation, {
    onError(error) {
      notification.error({
        message: 'Error creating order',
        description: error.message,
      });
    },
  });

  const proceedOrderCreation = () => {
    const supplierId = user?.id;

    if (!supplierId) {
      notification.error({
        message: 'Authentication required',
        description: 'Please sign in to place an order.',
      });
      return;
    }

    const orderPaymentMethod = isPayOnDelivery
      ? OrderPaymentMethod.PaymentOnDelivery
      : OrderPaymentMethod.PaymentOnline;

    const orderPaymentStatus = isPayOnDelivery
      ? OrderPaymentStatus.Pending
      : OrderPaymentStatus.Pending;

    createOrder({
      variables: {
        input: {
          products:
            cart?.products?.map((p) => ({
              product_id: p.productSKU ?? '',
              qty: p.productQty,
              profitAdded: Number(p.profitAdded ?? 0),
              price: Number(p.newPrice ?? 0),
              discount: Number(p.discountAmount ?? 0),
            })) ?? [],
          reseller: supplierId,
          total_amount: finalAmount,
          address_id: cart?.customer?.address_id,
          couponCode: cart?.coupon?.coupon?.code ?? '',
          coupon_id: cart?.coupon?.coupon_id,
          delivery_id: String(cart?.delivery?.id ?? cart?.pickup?.id ?? ''),
          delivery_type: _isPickup
            ? OrderDeliveryType.Pickup
            : OrderDeliveryType.Delivery,
          customer_id: cart?.customer?.customerId as string,
          customer_paid: cart?.customer_paid ?? false,
          source: 'SUPPLIER CONSOLE',
          delivery_amount: cart?.delivery?.price ?? 0,
          discount: appliedDiscount,
          payment_status: orderPaymentStatus,
          payment_type: orderPaymentMethod,
          supplier_id: _.last(cart?.products)?.supplier_id ?? undefined,
          free_delivery: isFreeDelivery,
        },
      },
    })
      .then((res) => {
        if (res.data?.createOrder) {
          dispatch(clearCart());
          navigate({ to: '/cart/success' });
          notification.success({
            message: 'Order placed',
            description: 'Your order has been placed successfully',
          });
        }
      })
      .catch((err) => {
        notification.error({
          message: 'Order failed',
          description: err.message,
        });
      });
  };

  const onCheckOut = () => {
    if (!user?.id) {
      notification.warning({
        message: 'Sign in required',
        description: 'Please sign in to place an order.',
      });
      return;
    }

    if (!cart?.customer?.customerId) {
      notification.warning({
        message: 'Customer required',
        description: 'Please add a customer before placing an order.',
      });
      return;
    }

    if (isPayOnDelivery) {
      proceedOrderCreation();
      return;
    }

    // "Pay Now" — Paystack integration to be added later
    if (country === 'GH' && (paymentMethod === 'pay_online' || cart?.delivery?.isPayOnline)) {
      setOpenPaymentMethodModal(true);
    } else {
      paymentRef.current?.execute();
    }
  };

  return (
    <ScreenWrapper showBackBtn title="Checkout">
      <div className="w-full max-w-2xl mx-auto bg-white min-h-screen mt-2 px-4 sm:px-6 lg:px-8 font-nunito">
        <SummaryCard cart={cart as ICart} isFreeDelivery={isFreeDelivery} />
        <CustomerCard customer={(cart?.customer as ICustomer) ?? {}} />
        <DeliveryDetailsCard
          cart={cart as ICart}
          isFreeDelivery={isFreeDelivery}
        />

        {/* Payment Type — forced pay-online banner when delivery hub requires it */}
        {cart?.delivery && cart?.delivery?.isPayOnline && (
          <div className="mt-4">
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Payment Type
            </h3>
            <div className="p-3 bg-purple-700 text-white rounded-lg flex flex-col gap-1">
              <span className="text-sm font-medium">💳 Pay Now</span>
              <span className="text-xs">
                Based on your selected delivery location. You will be required
                to pay the total amount of the order -{' '}
                <b>
                  {firstProductCurrency} {finalAmount.toFixed(2)}
                </b>{' '}
                online. Charges may apply.
              </span>
            </div>
          </div>
        )}

        {/* Payment Type — radio selection */}
        {(cart?.pickup || (cart?.delivery && !cart?.delivery?.isPayOnline)) && (
          <div className="mt-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Payment Type
            </h3>

            <div className="space-y-3">
              {/* Pay on Delivery */}
              <label
                className={`flex items-center justify-between w-full rounded-xl border p-4 cursor-pointer transition-all ${
                  paymentMethod === 'pay_on_delivery' ||
                  paymentMethod === 'pay_at_pickup'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
                onClick={() =>
                  dispatch(
                    setPaymentMethod(
                      cart?.delivery != null
                        ? 'pay_on_delivery'
                        : 'pay_at_pickup'
                    )
                  )
                }
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      paymentMethod === 'pay_on_delivery' ||
                      paymentMethod === 'pay_at_pickup'
                        ? 'border-blue-600'
                        : 'border-gray-300'
                    }`}
                  >
                    {(paymentMethod === 'pay_on_delivery' ||
                      paymentMethod === 'pay_at_pickup') && (
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    )}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">
                      Payment on Delivery
                    </span>
                    <span className="text-xs text-gray-500">
                      Pay with mobile money services including MTN, Vodafone,
                      and AirtelTigo
                    </span>
                    <span className="text-xs text-gray-400">
                      Fees: 1.95% + GHS 0.05
                    </span>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-4">
                  {firstProductCurrency} {totalToCollect.toFixed(2)}
                </span>
              </label>

              {/* Pay Now */}
              <label
                className={`flex items-center justify-between w-full rounded-xl border p-4 cursor-pointer transition-all ${
                  paymentMethod === 'pay_online'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
                onClick={() => dispatch(setPaymentMethod('pay_online'))}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      paymentMethod === 'pay_online'
                        ? 'border-blue-600'
                        : 'border-gray-300'
                    }`}
                  >
                    {paymentMethod === 'pay_online' && (
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    )}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">
                      Pay Now
                    </span>
                    <span className="text-xs text-gray-500">
                      Pay with bank transfer, mobile money, and card
                    </span>
                    <span className="text-xs text-gray-400">
                      Fees: 1.95% + GHS 0.05
                    </span>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-4">
                  {firstProductCurrency} {totalToCollect.toFixed(2)}
                </span>
              </label>
              <div
                style={{
                  opacity: 0,
                  position: 'absolute',
                  pointerEvents: 'none',
                }}
              ></div>
            </div>
          </div>
        )}

        {/* Hidden Paystack Payment Button - used for pay_online payment method */}
        <DukePaymentButton
          ref={paymentRef}
          source="SUPPLIER CONSOLE"
          billingEmail={user?.email ?? ''}
          firstName={_.first(user?.name?.split(' ')) ?? ''}
          lastName={_.last(user?.name?.split(' ')) ?? ''}
          phone={phoneNumber}
          amount={totalToCollect}
          orderId=""
          onCancel={() => notification.error({ message: 'Payment cancelled' })}
          onSuccess={() =>
            notification.success({ message: 'Payment initiated' })
          }
        />

        {/* Customer already made payment */}
        {/* <div className="flex items-center justify-between gap-3 bg-gray-200 px-2 py-5 rounded-lg mt-4">
          <div className="flex flex-col">
            <span className="font-semibold">Customer already made payment</span>
            <span className="font-regular text-sm text-gray-600">
              Checking this means that the amount paid on the app will not be
              collected from your customer on delivery
            </span>
          </div>

          <Switch
            checked={cart?.customer_paid ?? false}
            onChange={(e) => dispatch(setCustomerPaid(e.currentTarget.checked))}
            className="bg-gray-400 rounded-2xl p-1"
            size="md"
          />
        </div> */}

        {/* Processing Fee */}
        {/* <div className="my-2 p-3 bg-blue-200 bg-opacity-10 rounded-sm border border-blue-100 text-blue-800 flex flex-col">
          <div className="flex flex-row gap-2">
            <InformationCircleIcon className="w-4 h-4 text-blue-500 shrink-0" />
            <p className="text-xs">Information of Processing Fee: </p>
          </div>

          <span className="text-xs mt-1">
            We charge a 10% processing fee on reseller profits only. This is to
            enable us cover the transaction costs involved in paying suppliers,
            logistics companies and resellers.
          </span>
        </div> */}

        <div className="flex my-3 w-full pb-6">
          <button
            type="button"
            className="w-full flex justify-center items-center gap-2 py-3 px-4 text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary-500 hover:bg-primary-300 text-white"
            onClick={onCheckOut}
            disabled={ordering}
          >
            {ordering ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden />
            ) : isPayOnDelivery ? (
              'Place Order'
            ) : (
              `Pay Now (${firstProductCurrency} ${totalToCollect.toFixed(2)})`
            )}
          </button>
        </div>
      </div>
      <Modal
        open={openPaymentMethodModal}
        setOpen={setOpenPaymentMethodModal}
        title="Payment Details"
        description="Provide Payment Details to continue with the order"
      >
        <form className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select mobile money provider</option>
              {data?.getSupportedProviders?.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              Payment Phone Number
            </label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter phone number for payment"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-gray-500">
              Enter the mobile money number to receive payment prompt
            </p>
          </div>
          <div className="space-y-2">
            <Input
              id="account-name"
              type="text"
              placeholder={verifying ? 'Please wait ...' : 'Account Name'}
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Button */}
          <button
            type="button"
            className="w-full bg-primary-400 hover:bg-primary-300 cursor-pointer p-3 rounded-md text-white"
            disabled={!accountName || isInitializingPayment}
            onClick={() => {
              setIsInitializingPayment(true);
              paymentRef?.current?.execute(
                provider.toLowerCase() as PaystackMobileMoneyProvider
              );
            }}
          >
            {`Pay ${firstProductCurrency} ${cart?.totalAmountToCollect?.toFixed(2)}`}
          </button>
        </form>
      </Modal>
    </ScreenWrapper>
  );
};

export default Checkout;
